import { useCreateDialogRequest, useCreateRoute } from 'components/create-dialog/CreateDialogContext';
import Loader from 'components/Loader';
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetTimecardByIdQuery } from 'store/api/timecards';
import { TimecardDto } from 'types/timecards';
import { getOptionsFromClassifier } from 'utils/formUtil';
import { useClassifiers } from 'utils/useClassifiers';
import { useTimecardPrecision } from 'utils/useTimecardPrecision';
import TimecardPageForm from './TimecardPageForm';
import { timecardSecondsToHours, toValueFactorOptions } from './utils/timecardUtils';

type LocationState = {
    timecardData?: TimecardDto;
};

const FinishTimecardPageForm = (): JSX.Element => {
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: ['TIMECARD_ACTIVITY_TYPE', 'REMUNERATION_CALC_TYPE', 'TIMECARD_VALUE_FACTORS'],
    });
    const { isTwoDecimalEntryEnabled } = useTimecardPrecision();

    // In the create dialog these are the dialog's, where the router's describe the page behind it.
    const { params, location } = useCreateRoute();
    const { id: timecardId } = params;
    const closeDialog = useCreateDialogRequest()?.close;
    const { timecardData: timecardFromNavigationState } = (location.state || {}) as LocationState;
    const {
        data: timecard,
        isLoading: isLoadingTimecard,
        isError: isTimecardError,
    } = useGetTimecardByIdQuery(parseInt(timecardId || '0'), { skip: timecardId === undefined });
    const navigate = useNavigate();

    const initialData = useMemo<TimecardDto | undefined>(() => {
        if (timecard) {
            if (isLoadingClassifiers) {
                return undefined;
            }
            return {
                id: timecard.id,
                description: timecard.description,
                employee: timecard.employee,
                employees: timecard.employee ? [timecard.employee] : [],
                matter: timecard.matter,
                isNonBillable: timecard.isNonBillable,
                preserveNonBillableHours: timecard.preserveNonBillableHours,
                multiplierRate: timecard.multiplierRate,
                multiplierTypeCode: timecard.multiplierTypeCode,
                practiceAreas: timecard.practiceAreas,
                timecardDate: timecard.timecardDate,
                // The stored billed time is authoritative. Overwriting it with the used time here used to
                // be harmless because the backend forced the two equal; since billed time became
                // editable it silently discarded an uplift the moment a draft was opened to finish.
                billedTimeInSeconds: timecard.billedTimeInSeconds,
                billedTimeInHours: String(
                    timecardSecondsToHours(timecard.billedTimeInSeconds ?? 0, isTwoDecimalEntryEnabled)
                ),
                totalTimeInSeconds: timecard.totalTimeInSeconds,
                totalTimeInHours: String(
                    timecardSecondsToHours(timecard.totalTimeInSeconds ?? 0, isTwoDecimalEntryEnabled)
                ),
                expenses: timecard.expenses,
                matterAssignment: timecard.matterAssignment ?? null,
                remunerationCalcType: timecard.remunerationCalcTypeCode,
                remunerationTimeInSeconds: timecard.remunerationTimeInSeconds ?? 0,
                remunerationTimeInHours: String(
                    timecardSecondsToHours(timecard.remunerationTimeInSeconds ?? 0, isTwoDecimalEntryEnabled)
                ),
                timecardActivityType:
                    (
                        getOptionsFromClassifier(classifiers['TIMECARD_ACTIVITY_TYPE']) as {
                            label: string;
                            value: string;
                        }[]
                    ).find((val) => val.value === timecard?.timecardActivityTypeCode) ?? null,
                billingUpliftValueFactors: toValueFactorOptions(timecard.billingUpliftValueFactors, classifiers),
                valueFactorsNotes: timecard.valueFactorsNotes ?? '',
                notes: timecard.notes ?? '',
            };
        }
        if (timecardFromNavigationState) {
            const navStateTimecard = timecardFromNavigationState;
            return {
                id: navStateTimecard.id,
                description: navStateTimecard.description,
                employee: navStateTimecard.employee
                    ? {
                          id: navStateTimecard.employee.id,
                          name: navStateTimecard.employee.name,
                      }
                    : null,
                employees: navStateTimecard.employee
                    ? [{ id: navStateTimecard.employee.id, name: navStateTimecard.employee.name }]
                    : [],
                matter: navStateTimecard.matter ? navStateTimecard.matter : null,
                isNonBillable: navStateTimecard.isNonBillable,
                preserveNonBillableHours: navStateTimecard.preserveNonBillableHours,
                multiplierRate: navStateTimecard.multiplierRate,
                multiplierTypeCode: navStateTimecard.multiplierTypeCode,
                practiceAreas: navStateTimecard.practiceAreas,
                timecardDate: navStateTimecard.timecardDate,
                billedTimeInSeconds: navStateTimecard.billedTimeInSeconds,
                billedTimeInHours: String(
                    timecardSecondsToHours(navStateTimecard.billedTimeInSeconds ?? 0, isTwoDecimalEntryEnabled)
                ),
                totalTimeInHours: String(
                    timecardSecondsToHours(navStateTimecard.totalTimeInSeconds ?? 0, isTwoDecimalEntryEnabled)
                ),
                totalTimeInSeconds: navStateTimecard.totalTimeInSeconds,
                matterAssignment: navStateTimecard.matterAssignment ?? null,
                timecardActivityType: navStateTimecard.timecardActivityType,
                remunerationCalcType: navStateTimecard.remunerationCalcType,
                remunerationTimeInSeconds: navStateTimecard.remunerationTimeInSeconds ?? 0,
                remunerationTimeInHours: String(
                    timecardSecondsToHours(navStateTimecard.remunerationTimeInSeconds ?? 0, isTwoDecimalEntryEnabled)
                ),
                billingUpliftValueFactors: navStateTimecard.billingUpliftValueFactors,
                valueFactorsNotes: navStateTimecard.valueFactorsNotes,
                notes: navStateTimecard.notes,
            };
        }
        return undefined;
    }, [classifiers, isTwoDecimalEntryEnabled, timecard, timecardFromNavigationState, isLoadingClassifiers]);

    useEffect(() => {
        // With nothing to finish the page leaves for the list. The create dialog closes instead: a navigation
        // there would move the page behind it and leave it open on a loader.
        const leave = closeDialog ?? (() => navigate('/timecards'));
        if (timecardId === undefined && timecardFromNavigationState === undefined) {
            leave();
            return;
        }
        // Leave instead of staying on <Loader /> indefinitely when the query for `timecardId` fails or
        // resolves to no record.
        if (
            timecardId !== undefined &&
            timecardFromNavigationState === undefined &&
            !isLoadingTimecard &&
            (isTimecardError || !timecard)
        ) {
            leave();
        }
    }, [timecardId, timecardFromNavigationState, navigate, closeDialog, isLoadingTimecard, isTimecardError, timecard]);

    if (initialData === undefined) {
        return <Loader />;
    }

    return <TimecardPageForm type="finish" initialData={initialData} timecard={timecard} />;
};

export default FinishTimecardPageForm;
